import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Navbar } from './Navbar';
import { MobileTabBar } from './MobileTabBar';
import { FloatingActionButton } from './FloatingActionButton';
import { OnboardingOverlay, useOnboarding } from './Onboarding';

export const CoachLayout = ({ 
  children, 
  onCreateLesson,
  onCreateAthlete,
  onCreateVenue,
  onStartOnboarding,
  showFab = true,
  fabHidden = false
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showOnboarding, startOnboarding, closeOnboarding, isNewUser } = useOnboarding();
  const [onboardingStep, setOnboardingStep] = useState(0);
  const pauseOnboardingRef = useRef(false);
  const getCreatePath = (basePath) => {
    if (location.pathname === basePath) {
      const params = new URLSearchParams(location.search);
      params.set('create', 'true');
      return `${basePath}?${params.toString()}`;
    }
    return `${basePath}?create=true`;
  };
  const handleCreateLesson = onCreateLesson || (() => navigate(getCreatePath('/lessons')));
  const handleCreateAthlete = onCreateAthlete || (() => navigate(getCreatePath('/athletes')));
  const handleCreateVenue = onCreateVenue || (() => navigate(getCreatePath('/venues')));
  const handleStartOnboarding = () => {
    setOnboardingStep(0);
    sessionStorage.setItem('onboarding_active', 'true');
    startOnboarding();
  };
  const handleOnboardingClose = () => {
    closeOnboarding();
    if (pauseOnboardingRef.current) {
      pauseOnboardingRef.current = false;
      return;
    }
    sessionStorage.removeItem('onboarding_active');
  };
  const handleOnboardingAction = (action) => {
    pauseOnboardingRef.current = true;
    if (action === 'showLessonModal') {
      handleCreateLesson();
    } else if (action === 'showAthleteModal') {
      handleCreateAthlete();
    } else if (action === 'showVenueModal') {
      handleCreateVenue();
    }
    closeOnboarding();
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('startOnboarding') === 'true') {
      handleStartOnboarding();
      params.delete('startOnboarding');
      navigate(
        { pathname: location.pathname, search: params.toString() },
        { replace: true }
      );
    }
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!isNewUser || showOnboarding) {
      return;
    }
    if (sessionStorage.getItem('onboarding_auto_shown') === 'true') {
      return;
    }
    sessionStorage.setItem('onboarding_auto_shown', 'true');
    handleStartOnboarding();
  }, [isNewUser, showOnboarding, handleStartOnboarding]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const hasOpenDialog = document.querySelector('[data-state="open"][role="dialog"]');
    if (!hasOpenDialog && document.body.classList.contains('dialog-open')) {
      document.body.classList.remove('dialog-open');
      const appScroll = document.querySelector('.app-scroll');
      if (appScroll) {
        appScroll.style.removeProperty('position');
        appScroll.style.removeProperty('top');
        appScroll.style.removeProperty('left');
        appScroll.style.removeProperty('right');
        appScroll.style.removeProperty('width');
        appScroll.style.removeProperty('overflow');
      }
      document.documentElement.style.removeProperty('overflow');
      document.body.style.removeProperty('position');
      document.body.style.removeProperty('top');
      document.body.style.removeProperty('left');
      document.body.style.removeProperty('right');
      document.body.style.removeProperty('width');
      document.body.style.removeProperty('height');
      document.body.style.removeProperty('overflow');
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handleAdvance = (event) => {
      const nextStep = event?.detail?.step;
      if (typeof nextStep !== 'number') {
        return;
      }
      if (sessionStorage.getItem('onboarding_active') !== 'true') {
        return;
      }
      setOnboardingStep(nextStep);
      startOnboarding();
    };
    window.addEventListener('onboarding:advance', handleAdvance);
    return () => {
      window.removeEventListener('onboarding:advance', handleAdvance);
    };
  }, [startOnboarding]);

  return (
    <div className="app-shell bg-[#09090b]">
      <Navbar onStartOnboarding={onStartOnboarding || handleStartOnboarding} />
      <main className="app-scroll has-tab-bar has-top-bar">
        {children}
      </main>
      {showFab && (
        <FloatingActionButton
          onCreateLesson={handleCreateLesson}
          onCreateAthlete={handleCreateAthlete}
          onCreateVenue={handleCreateVenue}
          hidden={fabHidden}
        />
      )}
      <MobileTabBar />
      {showOnboarding && (
        <OnboardingOverlay
          onClose={handleOnboardingClose}
          onAction={handleOnboardingAction}
          currentStepOverride={onboardingStep}
        />
      )}
    </div>
  );
};
